import React, { useState, useEffect } from 'react'
import { Navbar, Container, Nav } from 'react-bootstrap'
import ThemeSwitcher from './ThemeSwitcher'
import './Navigation.css'

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('about')

  const navigationItems = [
    { id: 'about', label: 'About', href: '#about' },
    { id: 'experience', label: 'Experience', href: '#experience' },
    { id: 'skills', label: 'Skills', href: '#skills' },
    { id: 'certifications', label: 'Certifications', href: '#certifications' },
    { id: 'blogs', label: 'Blog', href: '#blogs' },
    { id: 'contact', label: 'Contact', href: '#contact' }
  ]

  useEffect(() => {
    let ticking = false
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 40)
          
          // Update active section based on scroll position
          const sections = ['about', 'experience', 'skills', 'certifications', 'blogs', 'contact']
          const current = sections.find(section => {
            const element = document.getElementById(section)
            if (element) {
              const rect = element.getBoundingClientRect()
              return rect.top <= 120 && rect.bottom >= 120
            }
            return false
          })
          
          if (current && current !== activeSection) {
            setActiveSection(current)
          }
          
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [activeSection])

  const handleNavClick = (sectionId, event) => {
    event.preventDefault()
    setActiveSection(sectionId)
    setIsMobileMenuOpen(false)

    // Smooth scroll to section
    const element = document.getElementById(sectionId)
    if (element) {
      const offset = 80 // Fixed navbar height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset - offset
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      })
    }
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  return (
    <>
      <Navbar 
        className={`modern-navbar ${isScrolled ? 'scrolled' : ''}`} 
        expand="lg" 
        fixed="top"
      >
        <Container fluid className="px-3 px-lg-5">
          <Navbar.Brand href="/" className="navbar-brand-modern" aria-label="Home">
            <div className="brand-logo-wrapper">
              <img
                alt="Nithin Dasyam Logo"
                src={process.env.PUBLIC_URL + '/assets/images/logo.jpeg'}
                className="brand-logo"
                loading="lazy"
                decoding="async"
                onError={e => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = process.env.PUBLIC_URL + '/assets/images/logo512.jpeg'
                }}
              />
            </div>
            <span className="brand-text">Nithin Dasyam</span>
          </Navbar.Brand>

          {/* Desktop Navigation */}
          <Nav className="desktop-nav ms-auto align-items-center">
            {navigationItems.map(item => (
              <Nav.Link
                key={item.id}
                href={item.href}
                className={`nav-link-modern ${activeSection === item.id ? 'active' : ''}`}
                onClick={(e) => handleNavClick(item.id, e)}
              >
                {item.label}
              </Nav.Link>
            ))}
            <div className="nav-divider" />
            <ThemeSwitcher />
          </Nav>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`} />
          </button>
        </Container>
      </Navbar>

      {/* Mobile Navigation Overlay */}
      <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-content">
          <button 
            className="mobile-nav-close"
            onClick={toggleMobileMenu}
            aria-label="Close navigation menu"
          >
            &times;
          </button>
          <nav className="mobile-nav-menu">
            {navigationItems.map(item => (
              <a
                key={item.id}
                href={item.href}
                className={`mobile-nav-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={(e) => handleNavClick(item.id, e)}
              >
                {item.label}
              </a>
            ))}
            <div className="mobile-nav-divider" />
            <ThemeSwitcher isMobile />
          </nav>
        </div>
      </div>
    </>
  )
}

export default Navigation
